<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import TabBar from "@/components/TabBar.vue";
import { getPendingInvites } from "@/api/chat";
import { ChatPlugin } from "@/plugins/chat";
import { Capacitor } from "@capacitor/core";
import UpdateDialog from "@/components/UpdateDialog.vue";
import { versionCheckState, checkAppVersion } from "@/utils/versionCheck";

const route = useRoute();
const router = useRouter();
const showTabBar = computed(() => route.meta.showTabBar ?? false);

const userStore = useUserStore();
const isInitComplete = ref(false);

// ====================== RTC 来电检测逻辑 ======================
/**
 * 轮询间隔（毫秒）
 */
const POLL_INTERVAL = 3000; // 3秒轮询一次

/**
 * 轮询定时器
 */
let pollTimer = null;

/**
 * 轮询是否已停止
 */
let isPollingStopped = false;

/**
 * 当前正在处理的邀请ID（避免重复弹窗）
 */
let currentInviteId = null;

/**
 * 检查待处理的通话邀请
 */
const redirectAfterPayment = (payReturn) => {
  const path = sessionStorage.getItem('pendingPaymentRedirectPath');
  const orderNo = sessionStorage.getItem('pendingPaymentOrderNo');
  sessionStorage.removeItem('pendingPaymentRedirectPath');
  sessionStorage.removeItem('pendingPaymentOrderNo');
  if (path) {
    router.replace({ path, query: orderNo ? { orderNo, payReturn } : { payReturn } });
  } else {
    router.replace({ path: '/order/list', query: { payReturn } });
  }
};

const handlePendingPaymentRedirect = () => {
  if (document.visibilityState === 'hidden') {
    if (localStorage.getItem('pendingAlipayPaymentRedirect') === '1') {
      localStorage.setItem('pendingAlipayPaymentBackgrounded', '1');
    }
    return false;
  }

  if (sessionStorage.getItem('pendingWechatPaymentRedirect') === '1') {
    console.log('💳 检测到待处理微信支付跳转');
    sessionStorage.removeItem('pendingWechatPaymentRedirect');
    redirectAfterPayment('wechat');
    return true;
  }

  if (
    localStorage.getItem('pendingAlipayPaymentRedirect') === '1' &&
    localStorage.getItem('pendingAlipayPaymentBackgrounded') === '1'
  ) {
    console.log('💳 检测到待处理支付宝支付跳转');
    localStorage.removeItem('pendingAlipayPaymentRedirect');
    localStorage.removeItem('pendingAlipayPaymentBackgrounded');
    redirectAfterPayment('alipay');
    return true;
  }

  return false;
};

const checkPendingInvites = async () => {
  console.log('📡 执行 checkPendingInvites');

  // 如果轮询已停止，不再执行
  if (isPollingStopped) {
    console.log('⚠️ 轮询已停止，跳过检查');
    return;
  }

  // 未登录或非 iOS 平台，不执行
  const currentToken = userStore.token;
  if (!currentToken) {
    console.log('⚠️ 未登录，跳过检查');
    return;
  }
  if (Capacitor.getPlatform() !== 'ios') {
    console.log('⚠️ 非 iOS 平台，跳过检查');
    return;
  }

  try {
    console.log('📡 调用 getPendingInvites API...');

    // 发出请求前再次检查 token 是否仍然有效
    if (!userStore.token || isPollingStopped) {
      console.log('⚠️ 请求前 token 已失效，跳过');
      return;
    }

    const response = await getPendingInvites();
    console.log('📡 API 原始响应:', response);
    console.log('📡 API 响应类型:', typeof response);
    console.log('📡 API 响应是否为数组:', Array.isArray(response));

    const invites = Array.isArray(response) ? response : (response?.data || []);
    console.log('📡 解析后的邀请数量:', invites.length);

    if (invites.length > 0) {
      console.log('📡 第一个邀请的完整数据:', JSON.stringify(invites[0], null, 2));
      console.log('📡 第一个邀请的 roomId:', invites[0].roomId);
      console.log('📡 第一个邀请的 callerName:', invites[0].callerName);
    }

    if (invites.length > 0) {
      console.log('📡 获取到待处理邀请:', invites.length, '条');

      // 只处理最新的一个邀请
      const invite = invites[0];

      // 避免重复处理同一个邀请
      if (currentInviteId === invite.inviteId) {
        console.log('📡 邀请已处理过，跳过:', invite.inviteId);
        return;
      }

      console.log('📞 收到 RTC 语音通话邀请:', invite);

      // 调用原生来电弹窗
      if (Capacitor.isNativePlatform()) {
        try {
          console.log('📞 准备调用 ChatPlugin.showIncomingCall...');
          await ChatPlugin.showIncomingCall({
            inviteId: String(invite.inviteId),  // 使用 inviteId 防止重复
            roomId: invite.roomId,
            callerName: invite.callerName || '健康师',
            callerAvatar: invite.callerAvatar || '',
          });

          // 标记已处理的邀请ID
          currentInviteId = invite.inviteId;
          console.log('✅ 已调用来电弹窗:', invite.inviteId);
        } catch (err) {
          console.error('❌ 来电弹窗调用失败：', err);
        }
      } else {
        console.log('⚠️ 非原生平台，跳过来电弹窗');
      }
    } else {
      // 没有待处理的邀请，清空当前邀请ID（允许下次弹窗）
      if (currentInviteId !== null) {
        console.log('📡 没有待处理邀请，重置状态');
        currentInviteId = null;

        // 通知原生层关闭来电界面
        if (Capacitor.isNativePlatform()) {
          try {
            await ChatPlugin.dismissIncomingCall();
            console.log('✅ 已通知原生层关闭来电界面');
          } catch (err) {
            console.error('❌ 关闭来电界面失败：', err);
          }
        }
      }
    }
  } catch (err) {
    console.error('❌ 检查待处理邀请失败：', err);
  }
};

/**
 * 启动轮询
 */
const startPolling = () => {
  if (pollTimer) return;

  isPollingStopped = false;
  console.log('📡 启动通话邀请轮询，间隔:', POLL_INTERVAL, 'ms');

  // 立即执行一次
  checkPendingInvites();

  // 定时轮询
  pollTimer = setInterval(() => {
    checkPendingInvites();
  }, POLL_INTERVAL);
};

/**
 * 停止轮询
 */
const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
    isPollingStopped = true;
    currentInviteId = null;
    console.log('🛑 停止通话邀请轮询');
  }
};

onMounted(async () => {
  console.log('🚀 App.vue onMounted 开始执行')

  await userStore.init();
  isInitComplete.value = true;

  // 启动页逻辑已移至路由守卫处理

  // iOS 平台且已登录，启动轮询
  const platform = Capacitor.getPlatform();
  console.log('📱 当前平台:', platform);
  console.log('📱 Capacitor.isNativePlatform():', Capacitor.isNativePlatform());
  console.log('📱 用户 token:', userStore.token ? '有' : '无');

  if (platform === 'ios' && userStore.token) {
    console.log('✅ 启动通话邀请轮询');
    startPolling();
  } else {
    console.log('⚠️ 不启动轮询 - platform:', platform, ', hasToken:', !!userStore.token);
  }

  window.addEventListener('focus', handlePendingPaymentRedirect);
  window.addEventListener('pageshow', handlePendingPaymentRedirect);
  document.addEventListener('visibilitychange', handlePendingPaymentRedirect);

  // iOS 禁用双击滚动
  if (platform === 'ios') {
    let lastTouchEnd = 0;

    document.addEventListener('touchend', function (event) {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, { passive: false });
  }

  // 检查APP版本更新（原生平台）
  console.log('🔍 版本检测准备 - Capacitor.isNativePlatform():', Capacitor.isNativePlatform())

  if (Capacitor.isNativePlatform()) {
    console.log('✅ 原生平台，开始版本检测流程')

    // 获取当前版本号（从配置读取）
    const { getCurrentVersion } = await import('@/api/appVersion')
    const currentVersion = await getCurrentVersion()
    versionCheckState.value.currentVersion = currentVersion
    console.log('📱 当前APP版本:', currentVersion)

    // 延迟1秒检测，避免影响启动速度
    setTimeout(() => {
      console.log('⏰ 1秒后开始检测版本...')
      checkAppVersion('customer');
    }, 1000);
  } else {
    console.log('🌐 非原生平台，跳过版本检测 - 请在真机或模拟器上测试')
  }

  console.log('✅ App.vue onMounted 执行完成')
});

onUnmounted(() => {
  stopPolling();
  window.removeEventListener('focus', handlePendingPaymentRedirect);
  window.removeEventListener('pageshow', handlePendingPaymentRedirect);
  document.removeEventListener('visibilitychange', handlePendingPaymentRedirect);
});

// 监听用户登录状态变化，启动/停止轮询
watch(() => userStore.token, () => {
  // 直接从 localStorage 检查 token，如果为空就不轮询
  const hasToken = !!localStorage.getItem('token');
  if (hasToken && Capacitor.getPlatform() === 'ios') {
    startPolling();
  } else {
    stopPolling();
  }
});

// 监听路由变化
watch(
  () => route.path,
  () => {
    const appContainer = document.getElementById("app");
    appContainer?.scrollTo(0, 0);
  }
);
</script>

<template>
  <router-view v-slot="{ Component }">
    <component :is="Component" />
  </router-view>
  <TabBar v-if="showTabBar" />

  <!-- 版本更新弹窗 -->
  <UpdateDialog
    v-model:show="versionCheckState.showUpdateDialog"
    :version-info="versionCheckState.versionInfo || {}"
    :current-version="versionCheckState.currentVersion"
  />
</template>

<style scoped>
</style>
